/* eslint-disable max-len */
/**
 * シフト編成（新）の状態・API・保存
 *
 * 9/24 の打合せで「一時保存 → 反映」のクッションをやめ、編集はその場で保存する形にした。
 *
 * - 保存済み（board.shifts）と、保存を待っている編集（items）を分けて持つ
 * - 編集はまず items に入れて画面にすぐ出し、裏で A3（一括反映）へ順に送る
 * - 保存は 1 本ずつ流す。送っている間の編集は items に溜まり、終わったら続けて送る
 * - 保存できたら盤面は読み直さない。画面の内容を正とし、サーバから受け取った ID と更新日時だけを手元の盤面に当てる（9/30。
 *   以前は保存のたびに読み直していて、続けて登録すると画面がちらついた）
 * - 失敗したら、そのセルだけ保存済みの内容に戻して「!」を付ける。ほかのセルは次の周で送り直す（元に戻すは作らない。9/24 決定）
 *
 * 人件費はここでは再計算しない。確定（公開）したときだけ、変更のあったスタッフを裏で再計算する（9/24 決定）。
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import useToastNotification from 'hooks/useToastNotification';
import {
  getBoard, getCommonSetting, saveBulk, saveCommonSetting,
} from 'api/shiftBoard';
import {
  ApiErrorBody, BoardData, DEFAULT_PERSONAL_SETTING, Delta, MoveRequest, Operation, PatternInfo,
  PersonalBoardSetting, PrintSetting,
  ShiftItem, SimulationItem, SimulationShift, TimeFormat, ViewType,
} from 'domain/attend/shiftBoard/types';
import {
  applyOperations, applySavedCells, buildItemsFromProduction, cellKey, findCell, fromShiftItem, isCellChanged, markChanged, rebaseOnSaved, replaceCell, toOperations, withProduction,
} from 'domain/attend/shiftBoard/simulation';
import {
  makeTimeFormatter, rangeForView, resolveSexagesimal, shiftAnchor, shiftCustomRange, toDateStr, ViewRange,
} from 'domain/attend/shiftBoard/time';
import { validateCell } from 'domain/attend/shiftBoard/validation';

/** 1 回の保存で送る操作の上限（サーバの MAX_OPERATIONS と同じ）。超えるときはセル単位で分けて順に送る（10/5） */
const MAX_SAVE_OPERATIONS = 200;

export interface ApiFailure {
  status: number;
  message: string;
  dates: Array<string>;
  lockReason?: string;
  index?: number | null;
  current?: unknown;
}

/** axios のエラーを 2-2 共通レスポンスに合わせて読む */
export const describeError = (err: any): ApiFailure => {
  const status: number = err && err.response ? err.response.status : 0;
  const body: ApiErrorBody = (err && err.response && err.response.data) || {};
  let message = body.message || '';
  let index: number | null | undefined;
  if (body.errors && body.errors.length > 0) {
    message = body.errors[0].message;
    index = body.errors[0].index;
  }
  if (!message) {
    message = status >= 500 || status === 0 ? 'サーバー側でエラーが発生しました。' : (err && err.message) || 'エラーが発生しました。';
  }
  return {
    status, message, dates: body.dates || [], lockReason: body.lockReason, index, current: body.current,
  };
};

/** 共通設定（common_setting）の機能コード。サーバの AttendShiftBoardService.FEATURE_SHIFT_BOARD と合わせる */
export const FEATURE_SHIFT_BOARD = 'shiftBoard';

const SESSION_ORG = 'shiftBoard.orgCode';
const SESSION_VIEW = 'shiftBoard.view';
const SESSION_DATE = 'shiftBoard.anchor';
const SESSION_CUSTOM = 'shiftBoard.customRange';

export type CellState = {
  staffCode: string;
  date: string;
  shifts: Array<SimulationShift>;
  changed: boolean;
};

/** 保存が終わったときに呼ぶ。送った操作と応答（確定し直した日など）を渡す */
export type SavedListener = (operations: Array<Operation>, delta: Delta) => void;

const useShiftBoard = (initialOrgCode?: string, initialDate?: string) => {
  const { errorNotification } = useToastNotification();

  const [orgCode, setOrgCodeState] = useState<string>(
    initialOrgCode || sessionStorage.getItem(SESSION_ORG) || sessionStorage.getItem('loginUser.orgCode') || '',
  );
  const [view, setViewState] = useState<ViewType>((sessionStorage.getItem(SESSION_VIEW) as ViewType) || 'half');
  const [anchor, setAnchorState] = useState<string>(initialDate || sessionStorage.getItem(SESSION_DATE) || toDateStr(new Date()));
  // 任意期間（view = 'custom' のときに使う）
  const [customRange, setCustomRangeState] = useState<ViewRange | null>(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_CUSTOM);
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed && parsed.from && parsed.to ? parsed : null;
    } catch (e) {
      return null;
    }
  });
  const [businessIds, setBusinessIds] = useState<Array<string>>([]);
  const [addStaffCodes, setAddStaffCodes] = useState<Array<string>>([]);
  const [board, setBoard] = useState<BoardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<ApiFailure | null>(null);
  // 自分だけの表示設定（人単位）と、店舗の設定のうち共通設定に置くもの（組織単位）。9/23 に分けた
  const [personal, setPersonal] = useState<PersonalBoardSetting>(DEFAULT_PERSONAL_SETTING);
  // 開いたときに「既定のビュー」を 1 回だけ当てるため。設定の読み込みが終わってから当てる
  const appliedDefaultView = useRef(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // 保存を待っている編集（null なら無い）
  const [items, setItemsState] = useState<Array<SimulationItem> | null>(null);
  const itemsRef = useRef<Array<SimulationItem> | null>(null);
  const setItems = useCallback((next: Array<SimulationItem> | null) => {
    itemsRef.current = next;
    setItemsState(next);
  }, []);
  const [saving, setSaving] = useState(false);
  // サーバへ送っている最中（読み直しは含まない）。「保存中…」の表示に使う（9/30）
  const [sending, setSending] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  // 保存に失敗したセル（キー → 理由）。セルに「!」を出す。そのセルを触り直すか、読み込み直すと消える（9/30）
  const [failed, setFailed] = useState<{ [key: string]: string }>({});
  const clearFailed = useCallback((keys: Array<string> | 'all') => {
    setFailed((prev) => {
      if (keys === 'all') {
        return Object.keys(prev).length === 0 ? prev : {};
      }
      if (!keys.some((k) => prev[k] !== undefined)) {
        return prev;
      }
      const next = { ...prev };
      keys.forEach((k) => {
        delete next[k];
      });
      return next;
    });
  }, []);
  // 送っている最中の保存。読み込みはこれが終わるのを待つ（別の期間の盤面で保存を計算しないため）
  const workerRef = useRef<Promise<void> | null>(null);
  const savedListener = useRef<SavedListener | null>(null);
  const boardRef = useRef<BoardData | null>(null);
  boardRef.current = board;

  const weekStartDow = board ? board.org.weekStartDow : 1;
  const monthStartDay = board ? board.org.monthStartDay : 1;
  const range: ViewRange = useMemo(
    () => (view === 'custom' && customRange ? customRange : rangeForView(view, anchor, weekStartDow, monthStartDay)),
    [view, anchor, weekStartDow, monthStartDay, customRange],
  );
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const setOrgCode = useCallback((code: string) => {
    sessionStorage.setItem(SESSION_ORG, code);
    setOrgCodeState(code);
  }, []);
  const setView = useCallback((v: ViewType) => {
    sessionStorage.setItem(SESSION_VIEW, v);
    setViewState(v);
  }, []);
  const setAnchor = useCallback((d: string) => {
    sessionStorage.setItem(SESSION_DATE, d);
    setAnchorState(d);
  }, []);
  const setCustomRange = useCallback((from: string, to: string) => {
    const next = from <= to ? { from, to } : { from: to, to: from };
    try {
      sessionStorage.setItem(SESSION_CUSTOM, JSON.stringify(next));
    } catch (e) {
      // 記憶できなくても表示はできる
    }
    setCustomRangeState(next);
    sessionStorage.setItem(SESSION_VIEW, 'custom');
    setViewState('custom');
  }, []);
  const moveRange = useCallback((direction: 1 | -1) => {
    if (view === 'custom' && customRange) {
      const next = shiftCustomRange(customRange, direction);
      setCustomRange(next.from, next.to);
      return;
    }
    setAnchor(shiftAnchor(view, range, direction));
  }, [view, range, customRange, setAnchor, setCustomRange]);

  // ------------------------------------------------------------------------------------------------------------ //
  // 読み込み

  // 盤面の読み込み（load）の通し番号。いちばん新しい読み込みの応答だけを盤面に入れる（10/5）
  const loadSeqRef = useRef(0);

  /**
   * 盤面を取り直す。画面は止めない（保存のたびに呼ぶので BlockUI を出さない）。
   * loadSeq は load からの読み込みだけ渡す（保存の周回の読み直しは、返り値を次の周回の基準に使うので渡さない）
   */
  const fetchBoard = useCallback(async (from: string, to: string, loadSeq?: number): Promise<BoardData> => {
    const data = await getBoard(orgCode, from, to, businessIds, addStaffCodes);
    // あとから別の読み込みが出ている、または表示している期間が変わっていたら、古い応答で盤面を戻さない
    // （設定の保存の直後に、半月の盤面が週の盤面を上書きしていた。10/5）
    if (loadSeq !== undefined
      && (loadSeq !== loadSeqRef.current || rangeRef.current.from !== from || rangeRef.current.to !== to)) {
      return data;
    }
    boardRef.current = data;
    setBoard(data);
    return data;
  }, [orgCode, businessIds, addStaffCodes]);

  const load = useCallback(async () => {
    if (!orgCode) {
      return;
    }
    // 通し番号は待つ前に取る。待っているあいだに新しい読み込みが出たら、こちらの応答は捨てる（10/5）
    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    // 保存を送っている最中なら終わるのを待つ。途中で期間を変えても、保存は元の期間の盤面で計算させる
    if (workerRef.current) {
      await workerRef.current;
    }
    setLoading(true);
    setLoadError(null);
    // 読み込み直したら画面は保存済みの内容になるので、失敗の印は消す
    clearFailed('all');
    try {
      // 一時保存（A19）は 9/24 に使わなくなった。以前の下書きが残っていても読まない。
      // 保存待ちの編集はここでは消さない（保存の周回が空にする。消すと送る前の編集が失われる）
      await fetchBoard(range.from, range.to, seq);
    } catch (e) {
      // 古い読み込みの失敗は出さない（新しい読み込みの結果を待つ）
      if (seq === loadSeqRef.current) {
        const f = describeError(e);
        setLoadError(f);
        errorNotification(f.message);
      }
    } finally {
      // 読み込み中の表示は、いちばん新しい読み込みが終わったときに消す（古い読み込みが先に終わると途中で消えていた）
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode, range.from, range.to, fetchBoard, clearFailed]);

  useEffect(() => {
    load();
  }, [load]);

  const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';

  /**
   * 設定を読む。組織が変わったときだけ
   *
   * 共通設定は company → org → staff の順に重ねて返るので、人単位の値があればそちらが勝つ。
   * 取れなくても既定値で動かす。
   */
  const loadCommonSetting = useCallback(async () => {
    if (!orgCode) {
      return;
    }
    try {
      const mine = await getCommonSetting(FEATURE_SHIFT_BOARD, orgCode, loginStaffCode);
      setPersonal({
        defaultView: (mine.defaultView as ViewType) || null,
        slotMinutes: typeof mine.slotMinutes === 'number' ? mine.slotMinutes : null,
        timeFormat: (mine.timeFormat as TimeFormat) || DEFAULT_PERSONAL_SETTING.timeFormat,
        printSetting: (mine.printSetting as Partial<PrintSetting>) || null,
      });
    } catch (e) {
      setPersonal(DEFAULT_PERSONAL_SETTING);
    } finally {
      setSettingsLoaded(true);
    }
  }, [orgCode, loginStaffCode]);

  useEffect(() => {
    loadCommonSetting();
  }, [loadCommonSetting]);

  /** 自分だけの表示設定を保存する（人単位。他の人には影響しない） */
  const savePersonal = useCallback(async (next: Partial<PersonalBoardSetting>) => {
    const merged = { ...personal, ...next };
    await saveCommonSetting(FEATURE_SHIFT_BOARD, 'staff', loginStaffCode, merged as any);
    setPersonal(merged);
  }, [personal, loginStaffCode]);


  /** 実際に使う値。人単位 → 店舗単位で保存されていた値（T1）→ 既定値 の順 */
  const effectiveSlotMinutes = personal.slotMinutes || (board ? board.setting.slotMinutes : 30);
  const effectiveDefaultView: ViewType = personal.defaultView || (board ? board.setting.defaultView : 'half');
  const effectivePrintSetting = personal.printSetting || (board ? board.setting.printSetting : null);

  // 「既定のビュー」は開いたときに効かせる。この画面で表示単位を選んだ人（セッションに残っている）には当てない
  useEffect(() => {
    if (appliedDefaultView.current || !board || !settingsLoaded) {
      return;
    }
    appliedDefaultView.current = true;
    if (!sessionStorage.getItem(SESSION_VIEW) && effectiveDefaultView && effectiveDefaultView !== view) {
      setViewState(effectiveDefaultView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, settingsLoaded, effectiveDefaultView]);

  /** 時間量を 60 進で出すか。設定が auto のときは勤怠の労務規約項目マスタに従う */
  const sexagesimal = resolveSexagesimal(personal.timeFormat, board ? board.org.timeDisplayFormat : 1);
  /** 時間量の整形。60 進/10 進と、10 進のときの丸めをマスタに合わせる（9/23） */
  const formatTime = useMemo(
    () => makeTimeFormatter(sexagesimal, board ? board.org.roundMethodOfThirdDecimalPlace : 0),
    [sexagesimal, board],
  );

  // ------------------------------------------------------------------------------------------------------------ //
  // 参照

  const production: Array<ShiftItem> = board ? board.shifts : [];
  const patternMap = useMemo(() => {
    const m: { [id: string]: PatternInfo } = {};
    if (board) {
      board.shiftPatterns.forEach((p) => {
        m[p.shiftPatternId] = p;
      });
    }
    return m;
  }, [board]);

  /** 保存を待っている編集があるか（送っている最中も含む） */
  const isSimulating = items !== null || saving;

  /** セルの現在の状態（保存待ちの編集があればそれ、無ければ保存済み） */
  const getCell = useCallback((staffCode: string, date: string): CellState => {
    // 保存待ちの一覧に無いセルは保存済みの内容を出す（9/30。一覧はいつも全セルを持つが、念のため空に見えないようにする）
    const c = items !== null ? items.filter((it) => it.staffCode === staffCode && it.targetDate === date)[0] : undefined;
    if (c) {
      return {
        staffCode, date, shifts: c.shifts, changed: !!c.changed,
      };
    }
    return {
      staffCode,
      date,
      shifts: production.filter((s) => s.staffCode === staffCode && s.targetDate === date && s.orgCode === orgCode).map(fromShiftItem),
      changed: false,
    };
  }, [items, production, orgCode]);

  /** 保存に失敗したセルの理由（無ければ null） */
  const failedOf = useCallback((staffCode: string, date: string): string | null => failed[cellKey(staffCode, date)] || null, [failed]);

  /** 他店支援（表示のみ） */
  const awayShifts = useCallback((staffCode: string, date: string): Array<ShiftItem> => production
    .filter((s) => s.staffCode === staffCode && s.targetDate === date && s.orgCode !== orgCode), [production, orgCode]);

  // ------------------------------------------------------------------------------------------------------------ //
  // 保存（9/24〜 編集はその場で保存する）

  /**
   * 保存待ちの編集を A3 へ順に送る。送っている最中に呼ばれたら何もしない（今の周回が拾う）
   *
   * 1 周: 保存待ちを写し取る → 保存済みとの差分を操作列にする → 送る → 応答の ID・更新日時を手元の盤面に当てる
   *       → 写し取ったあと触られていないセルと、保存済みと同じになったセルを保存待ちから外す
   *
   * 盤面は読み直さない（9/30）。読み直すと続けて登録している間に画面がちらつくため。確定して公開したとき・更新ボタン・期間の切り替えでは読み直す。
   *
   * 失敗したとき: A3 は 1 回の保存をまとめて取り消すので、失敗した操作のセルだけ保存済みの内容に戻して「!」を付け、
   * ほかのセルは次の周で送り直す。どのセルか分からない失敗（通信エラーなど）は、送ったセルをすべて戻す。
   * 他の人の変更と重なった（409）・ロックされた（423）ときは、最新を読み直す。
   *
   * 送っている間に同じセルをまた触ったときは、そのセルを残して次の周で送り直す。
   */
  const runSaveLoop = useCallback(() => {
    if (workerRef.current) {
      return;
    }
    const { from, to } = rangeRef.current;
    const belongOrgCodeOf = (staffCode: string): string => {
      const b = boardRef.current;
      const st = b ? b.staffs.filter((x) => x.staffCode === staffCode)[0] : undefined;
      return st ? st.belongOrgCode : orgCode;
    };
    const patternNameOf = (patternId: string | null): string | null => {
      const b = boardRef.current;
      const p = b && patternId ? b.shiftPatterns.filter((x) => x.shiftPatternId === patternId)[0] : undefined;
      return p ? p.name : null;
    };
    const run = async () => {
      setSaving(true);
      try {
        for (;;) {
          const snapshot = itemsRef.current;
          const { current } = boardRef;
          if (!snapshot || !current) {
            break;
          }
          let { operations } = toOperations(current.shifts, snapshot, current.days, orgCode);
          if (operations.length === 0) {
            setItems(null);
            break;
          }
          let sentCells = snapshot.filter((it) => isCellChanged(current.shifts, it, orgCode));
          if (operations.length > MAX_SAVE_OPERATIONS) {
            // 1 回の保存は 200 操作まで（10/5）。人数の多い店舗で前期間からコピー・曜日設定からの作成をすると超え、
            // まるごと保存エラーになっていた。同じセルの操作は同じ回に入れ、残りのセルは次の回で送る
            const batch: Array<SimulationItem> = [];
            let count = 0;
            sentCells.some((it) => {
              const n = toOperations(current.shifts, [it], current.days, orgCode).operations.length;
              if (batch.length > 0 && count + n > MAX_SAVE_OPERATIONS) {
                return true;
              }
              batch.push(it);
              count += n;
              return false;
            });
            sentCells = batch;
            ({ operations } = toOperations(current.shifts, batch, current.days, orgCode));
          }
          let delta: Delta;
          try {
            setSending(true);
            // 差分は作らせない（盤面は読み直さず、応答の ID・更新日時だけを使う）。差分づくりが保存の半分近くの時間を使っていた（9/30）
            // eslint-disable-next-line no-await-in-loop
            delta = await saveBulk({
              orgCode, unlockAndReconfirm: false, dateFrom: from, dateTo: to, operations, includeDelta: false,
            });
          } catch (e) {
            setSending(false);
            const f = describeError(e);
            const op = f.index !== null && f.index !== undefined ? operations[f.index] : undefined;
            const keys = op && op.staffCode && op.targetDate
              ? [cellKey(op.staffCode, op.targetDate)]
              : sentCells.map((it) => cellKey(it.staffCode, it.targetDate));
            errorNotification(`保存できませんでした。${f.message}（「!」の付いたセルは保存前の内容に戻しました）`);
            setFailed((prev) => {
              const next = { ...prev };
              keys.forEach((k) => {
                next[k] = f.message;
              });
              return next;
            });
            let base = current.shifts;
            if (f.status === 409 || f.status === 423) {
              try {
                // eslint-disable-next-line no-await-in-loop
                base = (await fetchBoard(from, to)).shifts;
              } catch (e2) {
                // 読み直せなくても、戻したセルは保存済みの内容で出す
              }
            }
            // 自分が直したセル（保存済みと違うセル）だけを残し、失敗したセルは外す。そのうえで保存済みの全セルに重ねる。
            // 読み直したとき（409/423）に手元の古い内容で他の人の変更を上書きしないよう、直していないセルは最新の内容を使う
            const failedSet: { [k: string]: boolean } = {};
            keys.forEach((k) => {
              failedSet[k] = true;
            });
            const edits = (itemsRef.current || []).filter((it) => !failedSet[cellKey(it.staffCode, it.targetDate)] && isCellChanged(current.shifts, it, orgCode));
            const reverted = withProduction(edits, base, orgCode);
            const remaining = markChanged(base, reverted, orgCode);
            setItems(remaining > 0 ? reverted : null);
            if (!op || remaining === 0) {
              break;
            }
            // eslint-disable-next-line no-continue
            continue;
          }
          // 保存できた時点で「保存しました」を出す
          setSending(false);
          setLastSavedAt(new Date());
          if (savedListener.current) {
            savedListener.current(operations, delta);
          }
          if (delta && delta.saved) {
            const nextBoard: BoardData = {
              ...current, shifts: applySavedCells(current.shifts, sentCells, delta.saved, orgCode, belongOrgCodeOf, patternNameOf),
            };
            boardRef.current = nextBoard;
            setBoard(nextBoard);
            const sent = new Set(sentCells);
            // 送っている間に触られたセル（と、分けて送るときの残りのセル）は、保存できたシフトの ID・更新日時を当て直してから次の回に回す（10/5）
            const rest = rebaseOnSaved(
              (itemsRef.current || []).filter((it) => !sent.has(it) && isCellChanged(nextBoard.shifts, it, orgCode)),
              sentCells, delta.saved,
            );
            // 残りのセルだけにせず、保存済みの全セルに重ねる（一部だけにすると無いセルが空に見え、追加で既存を消してしまう）
            const nextItems = rest.length > 0 ? withProduction(rest, nextBoard.shifts, orgCode) : null;
            if (nextItems) {
              markChanged(nextBoard.shifts, nextItems, orgCode);
            }
            setItems(nextItems);
            // eslint-disable-next-line no-continue
            continue;
          }
          // 応答に saved が無い（古いサーバ）ときだけ、これまでどおり読み直す
          try {
            // eslint-disable-next-line no-await-in-loop
            const fresh = await fetchBoard(from, to);
            const sent = new Set(sentCells);
            const rest = (itemsRef.current || []).filter((it) => !sent.has(it) && isCellChanged(fresh.shifts, it, orgCode));
            const nextItems = rest.length > 0 ? withProduction(rest, fresh.shifts, orgCode) : null;
            if (nextItems) {
              markChanged(fresh.shifts, nextItems, orgCode);
            }
            setItems(nextItems);
          } catch (e) {
            errorNotification('保存しました。最新の状態を読み込めなかったので、更新ボタンを押してください。');
            setItems(null);
            break;
          }
        }
      } finally {
        workerRef.current = null;
        setSending(false);
        setSaving(false);
      }
    };
    workerRef.current = run();
  }, [orgCode, fetchBoard, setItems, errorNotification]);

  // 保存が終わる前に画面を離れようとしたら止める
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (itemsRef.current || workerRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // ------------------------------------------------------------------------------------------------------------ //
  // 編集

  const commitItems = useCallback((next: Array<SimulationItem>) => {
    markChanged(production, next, orgCode);
    setItems(next);
    runSaveLoop();
  }, [production, orgCode, setItems, runSaveLoop]);

  const ensureItems = useCallback((): Array<SimulationItem> => (items !== null ? items : buildItemsFromProduction(production, orgCode)), [items, production, orgCode]);

  /** セルを丸ごと置き換える */
  const updateCell = useCallback((cell: SimulationItem) => {
    clearFailed([cellKey(cell.staffCode, cell.targetDate)]);
    commitItems(replaceCell(ensureItems(), cell));
  }, [commitItems, ensureItems, clearFailed]);

  /** セルに 1 件追加（検証つき）。失敗ならメッセージ */
  const addToCell = useCallback((staffCode: string, date: string, shift: SimulationShift): string | null => {
    const base = ensureItems();
    const cell = findCell(base, staffCode, date);
    const v = validateCell(cell.shifts, shift);
    if (!v.ok) {
      return v.message;
    }
    clearFailed([cellKey(staffCode, date)]);
    commitItems(replaceCell(base, { ...cell, shifts: [...cell.shifts, shift] }));
    return null;
  }, [ensureItems, commitItems, clearFailed]);

  /** セルの 1 件を削除（保存済みのものは deleted、仮のものは除去） */
  const removeFromCell = useCallback((staffCode: string, date: string, index: number) => {
    const base = ensureItems();
    const cell = findCell(base, staffCode, date);
    const shifts = cell.shifts.map((s, i) => (i === index ? { ...s, deleted: true } : s)).filter((s) => !(s.deleted && !s.shiftId));
    clearFailed([cellKey(staffCode, date)]);
    commitItems(replaceCell(base, { ...cell, shifts }));
  }, [ensureItems, commitItems, clearFailed]);

  /** セル間の移動・コピー */
  const moveBetweenCells = useCallback((from: { staffCode: string; date: string; index: number }, to: { staffCode: string; date: string }, copy: boolean): string | null => {
    const base = ensureItems();
    const src = findCell(base, from.staffCode, from.date);
    const shift = src.shifts[from.index];
    if (!shift) {
      return null;
    }
    const dst = findCell(base, to.staffCode, to.date);
    const moved: SimulationShift = {
      ...shift,
      shiftId: undefined,
      expectedUpdateDate: undefined,
      tempId: `m-${Date.now()}`,
      deleted: false,
      // 元のシフトの明細（休憩・業務の区切り）ごと写すため、元の ID を渡す（10/5）
      sourceShiftId: shift.shiftId || shift.sourceShiftId || null,
    };
    const v = validateCell(dst.shifts, moved);
    if (!v.ok) {
      return v.message;
    }
    let next = replaceCell(base, { ...dst, shifts: [...dst.shifts, moved] });
    if (!copy) {
      const srcShifts = src.shifts.map((s, i) => (i === from.index ? { ...s, deleted: true } : s)).filter((s) => !(s.deleted && !s.shiftId));
      next = replaceCell(next, { ...src, shifts: srcShifts });
    }
    clearFailed([cellKey(from.staffCode, from.date), cellKey(to.staffCode, to.date)]);
    commitItems(next);
    return null;
  }, [ensureItems, commitItems, clearFailed]);

  /** 自動作成・コピーの結果を保存する */
  const applyGenerated = useCallback((ops: Array<Operation>) => {
    const next = applyOperations(
      ensureItems(), ops,
      (id) => (id && patternMap[id] ? patternMap[id].shortName : null),
      (id) => (id && patternMap[id] ? patternMap[id].color : null),
      (id) => (id && patternMap[id] && patternMap[id].start && patternMap[id].end
        ? { start: patternMap[id].start as string, end: patternMap[id].end as string, isEndNextDay: patternMap[id].details.length > 0 && patternMap[id].details[patternMap[id].details.length - 1].isEndNextDay }
        : null),
    );
    clearFailed('all');
    commitItems(next);
  }, [ensureItems, commitItems, patternMap, clearFailed]);

  /** 保存が終わったときに呼ぶ処理を登録する（確定し直した日の再計算・雇用形態の警告など） */
  const onSaved = useCallback((listener: SavedListener | null) => {
    savedListener.current = listener;
  }, []);

  /**
   * バーの移動・伸縮（日ビュー）
   *
   * 9/30 から A4 を使わず、ほかの編集と同じく保存の周回（A3）に流す。A4 のあとは盤面を読み直していて画面がちらついたため。
   * 時刻を変えたシフトはパターンから外れてフリーシフトになる（A4 と同じ）。
   */
  const moveBar = useCallback(async (req: Omit<MoveRequest, 'orgCode' | 'dateFrom' | 'dateTo'>, staffCode: string, date: string, index: number) => {
    const base = ensureItems();
    const cell = findCell(base, staffCode, date);
    const shifts = cell.shifts.map((s, i) => (i === index ? {
      ...s, start: req.start, end: req.end, isEndNextDay: req.isEndNextDay, shiftPatternId: null, shiftPatternShortName: null,
    } : s));
    clearFailed([cellKey(staffCode, date)]);
    commitItems(replaceCell(base, { ...cell, shifts }));
  }, [ensureItems, commitItems, clearFailed]);

  return {
    orgCode,
    setOrgCode,
    view,
    setView,
    anchor,
    setAnchor,
    range,
    moveRange,
    setCustomRange,
    businessIds,
    setBusinessIds,
    addStaffCodes,
    setAddStaffCodes,
    board,
    loading,
    loadError,
    reload: load,
    // 共通設定・時間表記（9/16 決定）
    personal,
    savePersonal,
    slotMinutes: effectiveSlotMinutes,
    printSetting: effectivePrintSetting,
    sexagesimal,
    formatTime,
    // 保存（9/24〜 その場で保存）
    isSimulating,
    items,
    saving,
    sending,
    lastSavedAt,
    onSaved,
    getCell,
    failedOf,
    awayShifts,
    updateCell,
    addToCell,
    removeFromCell,
    moveBetweenCells,
    applyGenerated,
    moveBar,
    patternMap,
    cellKey,
  };
};

export type ShiftBoardController = ReturnType<typeof useShiftBoard>;

export default useShiftBoard;
